import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { parse } from 'query-string';
import { stringify } from 'qs';
import { useHistory } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import SortSelector from '../filters/SortSelector';
import ShowMap from '../filters/ShowMap';
import { updateSearchFilters as update } from '../../../actions';
import {
  DailyPrice,
  Size,
  ProjectType,
  MoreFilters,
  MoreFiltersOverlay,
  SelectorGroup,
  Dates,
} from '../filters';
import SizeSlider from '../filters/Size/SizeSlider';

import PriceSlider from '../filters/DailyPrice/PriceSlider';
import ProjectTypeList from '../filters/ProjectType/ProjectTypeList';
import Duration from '../filters/Duration';
import { toUrlParams } from '../../../helpers/searchParams';
import { Breakpoint } from '../../../helpers/MediaQuery';
import { t } from '../../../helpers/t';

import { usePrevious } from '../../../helpers/utils';

let firstSearch = true;

const FilterBar = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { parameters, page, moreFiltersOpen } = useSelector((state) => state.searchFilters);
  const { listingFeatures, spaceTypes, positionTypes } = useSelector((state) => state.env);

  useEffect(() => {
    if (!firstSearch) {
      firstSearch = false;
      const queryParams = window.searchCoreMarket || parse(
        history.location.search.slice(1),
        {
          parseNumbers: true,
          parseBooleans: true,
          arrayFormat: 'none',
        },
      );
      dispatch(update(queryParams));
    }
  }, [history, dispatch]);

  const previousParams = usePrevious(parameters);

  useEffect(() => {
    if (previousParams || (parameters && !isEqual(previousParams, parameters))) {
      const searchHandler = toUrlParams(parameters, page);
      let searchString = `/search?${stringify(searchHandler, { arrayFormat: 'repeat' })}`;
      const langPathMatch = history.location.pathname.match(/^\/([a-z]{2})($|\/)/);
      const language = langPathMatch ? langPathMatch[1] : null;
      if (language && language !== 'en') {
        searchString = `/${language}${searchString}`;
      }
      history.push(searchString);
    }
  }, [parameters, page]);

  const updateSearchFilters = (filterParameter) => dispatch(update(filterParameter));

  return (
    <div className="react-filter-bar-wrapper" data-scroll-anchor-to-footer="140">
      <div className="react-filter-bar">
        <Dates />

        <Breakpoint greaterThanOrEqual="priceFilterBreak">
          <DailyPrice />
        </Breakpoint>

        <Breakpoint greaterThanOrEqual="sizeFilterBreak">
          <Size />
        </Breakpoint>

        <Breakpoint greaterThanOrEqual="projectTypeFilterBreak">
          <ProjectType />
        </Breakpoint>

        <MoreFilters />

        <MoreFiltersOverlay>
          <SortSelector
            lessThan="sortSelectorBreak"
            size="large"
            sortOrder={parameters.s}
            updateSearchFilters={updateSearchFilters}
            filterName="sort"
            filterTitle={t('directives.listings.listingFilters.sortBy')}
          />
          { moreFiltersOpen
            ? (
              <Duration
                greaterThanOrEqual="xsmall"
                durationDays={parameters.duration}
                updateSearchFilters={updateSearchFilters}
                filterName="duration"
                filterTitle={t('directives.listings.listingFilters.duration.title')}
              />
            )
            : null}
          <PriceSlider
            lessThan="priceFilterBreak"
            filterName="price"
            filterTitle={t('directives.listings.listingFilters.dailyPrice')}
          />
          <SizeSlider
            lessThan="sizeFilterBreak"
            filterName="size"
            filterTitle={t('directives.listings.listingFilters.spaceSize')}
          />
          <ProjectTypeList
            lessThan="projectTypeFilterBreak"
            filterName="project-type"
            filterTitle={t('directives.listings.listingFilters.projectType')}
          />
          <SelectorGroup
            sortBy="label"
            greaterThanOrEqual="xsmall"
            filterName="space-type"
            filterTitle={t('directives.listings.listingFilters.spaceType')}
            parameterName="space_type_ids"
            options={spaceTypes}
          />
          <SelectorGroup
            sortBy="label"
            greaterThanOrEqual="xsmall"
            filterName="listing-feature"
            filterTitle={t('directives.listings.listingFilters.spaceFeatures')}
            parameterName="listing_feature_ids_contains_array"
            options={listingFeatures}
          />
          <SelectorGroup
            greaterThanOrEqual="xsmall"
            filterName="position-type"
            filterTitle={t('directives.listings.listingFilters.position')}
            parameterName="position_types"
            translationKeyPrefix="directives.forms.formRows.tags"
            options={positionTypes}
          />
        </MoreFiltersOverlay>
      </div>
      <Breakpoint greaterThanOrEqual="sortSelectorBreak" className="right-content">
        <SortSelector sortOrder={parameters.s} updateSearchFilters={updateSearchFilters} />
        <ShowMap />
      </Breakpoint>
    </div>
  );
};

export default FilterBar;
